import { useEffect, useState } from 'react';
import { FaPlus, FaRegCalendar } from 'react-icons/fa';
import { IoMdTime } from 'react-icons/io';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getCourseApi } from '../../../Api/Course';
import { getCourseTypeApi } from '../../../Api/CourseType';
import { addCourseOfferingApi, getCourseOfferingApi, updateCourseOfferingApi } from '../../../Api/CourseOffering';

interface Props {
    url: string;
    courseId: string | null;
    onClose: () => void;
}


export function CourseOfferingForm({ url, courseId, onClose }: Props) {
    interface Course {
        id: string;
        name: string;
    }
    type CourseTypeInterface = {
        id: string;
        name: string;
        parentId: string | null;
        availability: string;
        fees: number;
        timings: [{
            id: string;
            startime: string;
            endtime: string;
        }]
    };
    type TimingInterface = {
        id: string | null;
        time: string;
    };
    type CustomTime = {
        id: string | null;
        customStartTime: string;
        customEndTime: string;
        courseOfferingId: string | null;
    }

    const navigate = useNavigate();
    const location = useLocation();
    const id = courseId;

    const [Courses, setCourses] = useState<Course[]>([]);
    const [CourseTypes, setCourseTypes] = useState<CourseTypeInterface[]>([]);
    const [CourseNameId, setCourseNameId] = useState<string>("");
    const [CourseTypeId, setCourseTypeId] = useState<string>("");
    const [overrideCheckbox, setoverrideCheckbox] = useState(false);
    const [Fees, setFees] = useState(0);
    const [feeOverride, setFeeOverride] = useState(false);
    const [MultipleAvailability, setMultipleAvailability] = useState<TimingInterface[]>([]);
    const [customTime, setCustomTime] = useState<CustomTime[]>([{ id: null, customStartTime: '', customEndTime: '', courseOfferingId: null }]);
    const [timingsToDelete, setTimingsToDelete] = useState<string[]>([]);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEdit, setIsEditMode] = useState(false);

    const [courseType, setCourseType] = useState<CourseTypeInterface | undefined>(undefined);

    useEffect(() => {
        const foundCourseType = CourseTypes.find(type => type.id === CourseTypeId);
        setCourseType(foundCourseType);
    }, [CourseTypeId]);


    useEffect(() => {
        if (courseType && !feeOverride) {
            setFees(courseType.fees);
        }
    }, [courseType, feeOverride]);

    useEffect(() => {
        if (courseType && !overrideCheckbox) {
            const timings = courseType.timings.map(timing => ({ id: null, time: `${timing.startime}-${timing.endtime}` }));
            setMultipleAvailability(timings);
           
        }
    }, [courseType, overrideCheckbox]);

    const addTimeInput = () => {

        const Timing: TimingInterface = {
            id: null,
            time: `${customTime[0]?.customStartTime}-${customTime[0]?.customEndTime}`,

        };
        setMultipleAvailability([...MultipleAvailability, Timing]);
        setCustomTime([{ id: null, customStartTime: '', customEndTime: '', courseOfferingId: null }]);
    };

    const handleApiClick = async () => {
        try {
            const courseOfferingData: any = await getCourseOfferingApi(url);

            const courseData: any = await getCourseApi(url);
            setCourses(courseData);

            const courseTypeData: any = await getCourseTypeApi(url);
            const filteredCourseType = courseTypeData.filter(
                (item: CourseTypeInterface) => item.parentId === null
            );
            setCourseTypes(filteredCourseType);

            if (id) {
                const courseOfferingToEdit = courseOfferingData.find((course: any) => course.id === id);
                if (courseOfferingToEdit) {
                    setCourseNameId(courseOfferingToEdit.courseId || "");
                    setCourseTypeId(courseOfferingToEdit.courseTypeId || "");
                    if (courseOfferingToEdit.timings && courseOfferingToEdit.timings.length > 0) {
                        const updatedTimings = courseOfferingToEdit.timings.map((timing) => {
                            return { id: timing.id, time: `${timing.startTime}-${timing.endtime}` };
                        });
                        setMultipleAvailability(updatedTimings);
                        setoverrideCheckbox(true);
                    }
                    if (courseOfferingToEdit.fees) {
                        setFeeOverride(true);
                        setFees(courseOfferingToEdit.fees)
                    }
                    setIsEditMode(true);

                }
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    useEffect(() => {
        handleApiClick();
    }, []);

    const handleResetForm = () => {
        setCourseNameId("");
        setCourseTypeId("");
        // navigate(-1);
        onClose();
    };

    const handleSubmitClick = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!CourseNameId || !CourseTypeId) {
            toast.error("Please fill all the fields")
            return;
        }

        try {
            if (isEdit) {
                await updateCourseOfferingApi(id, CourseNameId, CourseTypeId, Fees, MultipleAvailability, timingsToDelete, url);
                toast.success("Course Offering updated successfully");
                onClose();
            } else {
                const finalTimings = MultipleAvailability.map(item => item.time);
                await addCourseOfferingApi(CourseNameId, CourseTypeId, Fees, finalTimings, url);
                toast.success("Course Offering added successfully");
                onClose()
            }
        } catch (error) {
            toast.error(isEdit ? "Failed to update Course Offering" : "Failed to add Course Offering");
            console.error("Error during submission:", error);
        }
    };

    const handleDeleteMultipleAvailability = async (deleteindex) => {

        if (isEdit === true) {
            const id = MultipleAvailability[deleteindex].id;
            if (id !== null) {
                setTimingsToDelete([...timingsToDelete, id]);
            }
            const updatedAvailability = MultipleAvailability.filter((_, i) => i !== deleteindex);
            setMultipleAvailability(updatedAvailability);
        }
        else {
            const updatedAvailability = MultipleAvailability.filter((_, i) => i !== deleteindex);
            setMultipleAvailability(updatedAvailability);
        }

    };

    return (
        <div className="">
            <div
                className={`transition duration-300 ${isModalOpen ? "filter blur-sm" : ""
                    }`}
            >
                <h1 className="text-3xl font-bold text-blue-600 mb-5">{isEdit ? "Edit Course Offering" : "Add Course Offering"}</h1>
                <form onSubmit={handleSubmitClick} className="mt-8 space-y-4">
                    <div className="flex flex-col gap-2">
                        <div className="flex mb-2 flex-col space-y-2">
                            <label
                                htmlFor="courseName"
                                className="block text-sm font-medium text-black dark:text-gray-200"
                            >
                                Course Name  <span className="text-red-500">*</span>
                            </label>
                            <select
                                id="courseName"
                                className="p-2 border border-gray-300 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-500"
                                value={CourseNameId}
                                onChange={(e) => setCourseNameId(e.target.value)}
                            >
                                <option value="" disabled>
                                    Select Course Name
                                </option>
                                {Courses.map((item) => (
                                    <option key={item.id} value={item.id}>
                                        {item.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="flex flex-col space-y-2">
                            <label
                                htmlFor="courseType"
                                className="block text-sm font-medium text-black dark:text-gray-200"
                            >
                                Course Type  <span className="text-red-500">*</span>
                            </label>
                            <select
                                id="courseType"
                                className="p-2 border border-gray-300 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-500"
                                value={CourseTypeId}
                                onChange={(e) => setCourseTypeId(e.target.value)}
                            >
                                <option value="" disabled>
                                    Select Course Type
                                </option>
                                {CourseTypes.map((item) => (
                                    <option key={item.id} value={item.id}>
                                        {item.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Course Type Details */}
                        {courseType ? (
                            <>
                                <div className="flex flex-col space-y-2 border p-4 rounded-lg">
                                    <div className="flex items-center justify-between">
                                        <h1 className="text-2xl font-bold">Course Type Details</h1>
                                        {/* <span className="px-2 py-1 text-sm bg-gray-100 text-gray-800 rounded-full">Fixed</span> */}
                                    </div>

                                    <div className="pl-7">

                                        <div className="flex items-center gap-2 mb-2">
                                            <h2 className="text-lg font-semibold">Fee</h2>
                                        </div>

                                        {/* OverRide checkbox  */}
                                        <label className="inline-flex items-center cursor-pointer mb-5">
                                            <input checked={feeOverride}
                                                id="bordered-checkbox-1"
                                                type="checkbox" value=""
                                                onChange={(e) => {
                                                    if (feeOverride === true) {
                                                        setFees(0);
                                                    } else if (feeOverride === false) {
                                                        setFees(courseType.fees);
                                                    }
                                                    setFeeOverride(e.target.checked);
                                                }}
                                                name="bordered-checkbox" className="sr-only peer" />
                                            <div className="relative w-9 h-5 bg-gray-400 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
                                            <span className="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">Override Course Type Fee</span>
                                        </label>

                                        <input
                                            id="Fees"
                                            type="number"
                                            required
                                            disabled={!feeOverride}
                                            placeholder="Fees"
                                            value={feeOverride ? Fees : courseType.fees ? courseType.fees : 0}
                                            onChange={(event) => setFees(Number(event.target.value))}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div className="space-y-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <FaRegCalendar className="h-5 w-5 text-gray-500" />
                                            <h2 className="text-lg font-semibold">Availability</h2>
                                        </div>
                                        <div className="pl-7">
                                            {courseType?.availability ? (
                                                <span className="px-2 py-1 text-sm border bg-gray-100 border-gray-300 text-gray-600 rounded-full">
                                                    {courseType?.availability || ''}
                                                </span>
                                            ) : (
                                                <div className="text-gray-500 text-center py-2">
                                                    Availability is not specified.
                                                </div>
                                            )
                                            }
                                        </div>
                                    </div>
                                    <hr className="my-6 border-t border-gray-200" />
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <IoMdTime className="h-5 w-5 text-gray-500" />
                                            <h2 className="text-lg font-semibold">Timings</h2>
                                        </div>
                                    </div>

                                    <div className="pl-7">
                                        {/* OverRide checkbox  */}
                                        <label className="inline-flex items-center cursor-pointer mb-5">
                                            <input checked={overrideCheckbox}
                                                id="bordered-checkbox-1"
                                                type="checkbox" value=""
                                                onChange={(e) => {
                                                    if (overrideCheckbox === true) {
                                                        if (isEdit) {
                                                            const ids = MultipleAvailability.filter(timing => timing.id !== null).map(timing => timing.id);
                                                            if (ids.length > 0) {
                                                                setTimingsToDelete([...timingsToDelete, ...ids]);
                                                            }
                                                        }
                                                        setMultipleAvailability([]);
                                                    } else if (overrideCheckbox === false) {

                                                        setMultipleAvailability(courseType?.timings.map(timing => ({ id: null, time: `${timing.startime}-${timing.endtime}` })));
                                                    }
                                                    setoverrideCheckbox(e.target.checked);
                                                }}
                                                name="bordered-checkbox" className="sr-only peer" />
                                            <div className="relative w-9 h-5 bg-gray-400 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
                                            <span className="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">Override Course Type Timings</span>
                                        </label>

                                        {overrideCheckbox === true && (
                                            <button
                                                type='button'
                                                onClick={() => setIsModalOpen(true)}
                                                className="max-w-fit mb-2 flex items-center justify-center px-4 py-2 text-sm tracking-wide text-white capitalize transition-colors duration-300 transform bg-blue-600 rounded-lg hover:bg-blue-500 focus:outline-none focus:ring focus:ring-blue-300 focus:ring-opacity-80"
                                            >
                                                <FaPlus className="mr-2 w-4" />
                                                Add Time
                                            </button>
                                        )}

                                        {MultipleAvailability.length > 0 ? (
                                            <div>
                                                <table className="mt-5 w-full border divide-y divide-gray-200 dark:divide-gray-700 md:rounded-lg">
                                                    <thead className="bg-gray-100 dark:bg-gray-800">
                                                        <tr>
                                                            <th className="px-4 py-3.5 border border-gray-300 text-md font-normal text-center ">Action</th>
                                                            <th className="px-4 py-3.5 border border-gray-300 text-md font-normal text-center">Start Time</th>
                                                            <th className="px-4 py-3.5 border border-gray-300 text-md font-normal text-center">End Time</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white divide-y divide-gray-200 dark:divide-gray-700 dark:bg-gray-900">
                                                        {MultipleAvailability.map((time, index) => (
                                                            <tr key={index}>
                                                                <td className="px-4 py-3.5 border border-gray-300 text-md font-normal text-center text-gray-500">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleDeleteMultipleAvailability(index)}
                                                                        className="text-red-500 hover:text-red-700"
                                                                    >
                                                                        <svg
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                            fill="none"
                                                                            viewBox="0 0 24 24"
                                                                            strokeWidth="1.5"
                                                                            stroke="currentColor"
                                                                            className="w-5 h-5"
                                                                        >
                                                                            <path
                                                                                strokeLinecap="round"
                                                                                strokeLinejoin="round"
                                                                                d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                                                                            />
                                                                        </svg>
                                                                    </button>
                                                                </td>
                                                                <td className="px-4 py-3.5 border border-gray-300 text-md font-normal text-center">{time.time && time.time.includes('-') ? time.time.split('-')[0] : time.time}</td>
                                                                <td className="px-4 py-3.5 border border-gray-300 text-md font-normal text-center">{time.time && time.time.includes('-') ? time.time.split('-')[1] : ''}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            courseType?.timings.length > 0 ? (
                                                <div>
                                                    <table className="mt-5 w-full lg:w-[80%] divide-y divide-gray-200 dark:divide-gray-700 md:rounded-lg">
                                                        <thead className="bg-gray-100 dark:bg-gray-800">
                                                            <tr>
                                                                <th className="px-3 py-2.5 border border-gray-300 text-md text-center text-gray-700">Start Time</th>
                                                                <th className="px-4 py-2.5 border border-gray-300 text-md text-center text-gray-700">End Time</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="bg-white divide-y divide-gray-200 dark:divide-gray-700 dark:bg-gray-900">
                                                            {courseType.timings.map((time, index) => (
                                                                <tr key={index}>
                                                                    <td className="px-4 py-3.5 border border-gray-300 text-md font-normal text-center">{time.startime}</td>
                                                                    <td className="px-4 py-3.5 border border-gray-300 text-md font-normal text-center">{time.endtime}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : (
                                                <div className="text-gray-500 text-center py-4">
                                                    No Timings Available
                                                </div>
                                            )
                                        )}
                                    </div>

                                </div>
                            </>) : ""
                        }

                    </div>

                    <div className="flex justify-start space-x-4">
                        <button
                            type="submit"
                            className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            {isEdit ? "Update" : "Save and close"}
                        </button>
                        <button
                            type="button"
                            onClick={handleResetForm}
                            className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
            {/* Side view */}
            {isModalOpen && (
                <div
                    className={`fixed inset-y-0 right-0 z-50 w-2/3 md:w-1/3 bg-white dark:bg-gray-800 p-6 shadow-lg transform transition-transform duration-300`}
                >
                    <div className="p-6">
                        <div className="flex justify-between">
                            <h2 className="text-xl font-bold  text-blue-600 mb-4 ">
                                Add TimeSlot
                            </h2>
                            <button
                                // onClick={handleCloseModal}
                                className="text-3xl text-gray-600 dark:text-gray-400 h-6 w-6"
                            >
                                &times;
                            </button>
                        </div>
                        <div className="flex justify-between">
                            <div className="w-1/2 pr-2">
                                <label htmlFor="customStartTime" className="block text-sm font-medium text-black dark:text-gray-200 mb-2">
                                    Start Time
                                </label>
                                <input
                                    type="time"
                                    id="customStartTime"
                                    value={customTime[0]?.customStartTime}
                                    onChange={(e) => setCustomTime([{ ...customTime[0], customStartTime: e.target.value }])}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                    required
                                />
                            </div>
                            <div className="w-1/2 pl-2">
                                <label htmlFor="customEndTime" className="block text-sm font-medium text-black dark:text-gray-200 mb-2">
                                    End Time
                                </label>
                                <input
                                    type="time"
                                    id="customEndTime"
                                    value={customTime[0]?.customEndTime}
                                    onChange={(e) => setCustomTime([{ ...customTime[0], customEndTime: e.target.value }])}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                    required
                                />
                            </div>
                        </div>
                        <div className="flex space-x-4 justify-start mt-4">
                            <button
                                onClick={() => {
                                    addTimeInput();
                                    setIsModalOpen(false);
                                }}
                                className="px-8 py-2.5 leading-5 text-white transition-colors duration-300 transform bg-blue-600 rounded-md hover:bg-blue-500 focus:outline-none focus:bg-gray-600"
                            >
                                Add

                            </button>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="px-8 py-2.5 leading-5 bg-gray-300 rounded-md"
                            >
                                Cancel
                            </button>
                        </div>

                    </div>
                </div>
            )}
        </div>
    );
}

export default CourseOfferingForm;
